<template>
  <div>
    <!-- Formulaire de connexion -->
    <form v-if="!adminAuth.adminConnecte" @submit.prevent="seConnecter" class="max-w-md p-6 bg-white rounded shadow">
      <p class="pb-2">Courriel<span class="text-red-500 text-2xl pl-2">*</span></p>
      <input
        type="email"
        placeholder="entrez votre email"
        v-model="email"
        class="w-full border p-2 rounded mb-4 border-primaire bg-slate-100"
        autocomplete="email"
        required
        aria-label="Courriel"
        title="Entrez votre adresse email"
      />

      <div class="relative mb-8 max-w-64">
        <p class="pb-2">Mot de passe<span class="text-red-500 text-2xl pl-2">*</span></p>
        <input
          :type="showPwd ? 'text' : 'password'"
          placeholder="entrez votre mot de passe"
          v-model="pwd"
          class="border p-2 rounded w-full border-primaire pr-10 bg-slate-100"
          autocomplete="current-password"
          required
          aria-label="Mot de passe"
          title="Entrez votre mot de passe"
        />
        <button
          type="button"
          @click="showPwd = !showPwd"
          class="absolute right-2 top-3/4 transform -translate-y-1/2 text-gray-500"
          tabindex="-1"
          aria-label="Afficher/Masquer le mot de passe"
        >
          <span v-if="showPwd">👁️</span>
          <span v-else>🙈</span>
        </button>
      </div>

      <input
        type="submit"
        :disabled="enCoursDeConnexion"
        class="bg-blue-500 text-white p-2 rounded cursor-pointer hover:bg-primaire disabled:bg-gray-400"
        :value="enCoursDeConnexion ? 'Connexion...' : 'Se connecter'"
        aria-label="Se connecter"
      />
    </form>

    <!-- Interface post-connexion (simplifiée pour admin) -->
    <div v-else-if="adminAuth.adminConnecte && !adminAuth.verificationTerminee" class="max-w-md p-6 bg-white rounded shadow">
      <p class="mb-4">🔄 Vérification des droits administrateur...</p>
    </div>

    <!-- Utilisateur connecté mais pas admin -->
    <div v-else-if="adminAuth.adminConnecte && adminAuth.verificationTerminee && !adminAuth.estAdmin" class="max-w-md p-6 bg-white rounded shadow">
      <p class="mb-4 text-red-600">❌ Accès refusé. Droits administrateur requis.</p>
      <p class="mb-4">Connecté en tant que : <strong>{{ adminAuth.admin?.email }}</strong></p>
      <button
        @click="deconnexion"
        class="bg-red-500 text-white p-2 rounded cursor-pointer hover:bg-red-600"
        aria-label="Déconnexion"
      >
        Déconnexion
      </button>
    </div>

    <!-- Admin valide connecté -->
    <div v-else-if="adminAuth.adminConnecte && adminAuth.verificationTerminee && adminAuth.estAdmin" class="max-w-md p-6 bg-white rounded shadow">
      <p class="mb-4 text-green-600">✅ Accès administrateur confirmé</p>
      <p class="mb-4">Connecté en tant que : <strong>{{ adminAuth.admin?.email }}</strong></p>
      
      <!-- Gestion du prénom (optionnel pour admin) -->
      <template v-if="editionPrenom && !prenom">
        <p class="mb-2">Prénom (optionnel) :</p>
        <input
          type="text"
          placeholder="Votre prénom"
          v-model="prenomTemp"
          class="border p-2 rounded mb-4 border-primaire text-black w-full"
          aria-label="Prénom"
        />
        <div class="flex gap-2 mb-4">
          <button
            @click="miseAJourUtilisateur"
            class="bg-green-500 text-white p-2 rounded cursor-pointer hover:bg-green-600"
          >
            Sauvegarder
          </button>
          <button
            @click="ignorerPrenom"
            class="bg-gray-500 text-white p-2 rounded cursor-pointer hover:bg-gray-600"
          >
            Ignorer
          </button>
        </div>
      </template>
      
      <template v-else>
        <p class="mb-4 text-xl">
          Bonjour <strong>{{ prenom || 'Administrateur' }}</strong>
        </p>
        <div class="flex gap-2">
          <button
            v-if="prenom"
            @click="editionPrenom = true; prenomTemp = prenom"
            class="bg-blue-500 text-white p-2 rounded cursor-pointer hover:bg-blue-600"
          >
            Modifier le prénom
          </button>
          <button
            @click="deconnexion"
            class="bg-red-500 text-white p-2 rounded cursor-pointer hover:bg-red-600"
          >
            Déconnexion
          </button>
        </div>
      </template>
    </div>

    <!-- Messages d'état -->
    <p v-if="connexionReussie" class="text-green-600 mb-2 mt-2">✅ Connexion réussie !</p>
    <p v-if="erreurConnexion" class="text-red-500 mb-2 mt-2">❌ {{ erreurConnexion }}</p>
  </div>
</template>

<script setup>
import { ref } from "vue";
import { useAdminAuth } from "../store/storeAdminAuth.js";
import { auth } from "../firebase.js";
import {
  signInWithEmailAndPassword,
  signOut,
  updateProfile,
} from "firebase/auth";

// Émettre un événement pour le composant parent
const emit = defineEmits(['login', 'logout']);

const adminAuth = useAdminAuth();

const email = ref("");
const pwd = ref("");
const showPwd = ref(false);
const prenom = ref("");
const prenomTemp = ref("");
const erreurConnexion = ref("");
const editionPrenom = ref(false);
const connexionReussie = ref(false);
const enCoursDeConnexion = ref(false);

const seConnecter = async () => {
  erreurConnexion.value = "";
  connexionReussie.value = false;
  enCoursDeConnexion.value = true;

  if (!email.value || !pwd.value) {
    erreurConnexion.value = "Veuillez remplir tous les champs.";
    enCoursDeConnexion.value = false;
    return;
  }

  try {
    // Émettre l'événement vers le parent avec les credentials
    emit('login', { email: email.value, password: pwd.value });
    
    connexionReussie.value = true;
    
    // Nettoyer les champs après connexion réussie
    email.value = "";
    pwd.value = "";
    
    setTimeout(() => {
      connexionReussie.value = false;
    }, 3000);
    
  } catch (error) {
    erreurConnexion.value = "Erreur lors de la connexion.";
    console.error("Erreur de connexion:", error);
  } finally {
    enCoursDeConnexion.value = false;
  }
};

const miseAJourUtilisateur = async () => {
  if (!adminAuth.admin) {
    console.log("Aucun utilisateur connecté.");
    return;
  }
  
  try {
    await updateProfile(adminAuth.admin, {
      displayName: prenomTemp.value,
    });
    prenom.value = prenomTemp.value;
    editionPrenom.value = false;
  } catch (error) {
    console.error("Erreur lors de la mise à jour:", error);
    erreurConnexion.value = "Erreur lors de la mise à jour du profil.";
  }
};

const ignorerPrenom = () => {
  editionPrenom.value = false;
  prenomTemp.value = "";
};

const deconnexion = async () => {
  try {
    emit('logout'); // Émettre vers le parent

    // Réinitialiser les champs locaux
    email.value = "";
    pwd.value = "";
    prenom.value = "";
    prenomTemp.value = "";
    editionPrenom.value = false;
    erreurConnexion.value = "";
    connexionReussie.value = false;

  } catch (error) {
    console.error("Erreur lors de la déconnexion:", error);
  }
};

// Initialiser le prénom si l'utilisateur est déjà connecté
if (adminAuth.admin?.displayName) {
  prenom.value = adminAuth.admin.displayName;
} else if (adminAuth.admin && !prenom.value) {
  editionPrenom.value = true;
}
</script>